<template>
  <div class="tool-detail-page white-theme pollo-layout">
    <div class="container">
      <section id="face-swap-tool" class="first-screen">
        <div class="breadcrumb">
          <router-link to="/">{{ $t('modelDetail.breadcrumb.home') }}</router-link><span>&rsaquo;</span><router-link to="/tools">{{ $t('tools.page.allTools') }}</router-link><span>&rsaquo;</span><span>{{ $t('tools.face-swap.name') }}</span>
        </div>
        <div class="first-screen-grid">
          <div class="action-column">
            <div v-if="!isLoggedIn" class="login-prompt"><div class="login-prompt-content"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg><h2>{{ $t('tools.detail.loginRequired') }}</h2><p>{{ $t('tools.detail.loginRequiredDesc') }}</p><button class="btn btn-primary" @click="goLogin">{{ $t('brandSeo.toolFreeTrialCta') }}</button></div></div>
            <div v-else class="tool-interface">
              <div v-if="processError" class="tool-error--block"><p class="tool-error">{{ processError }}</p></div>
              <div class="upload-grid">
          <!-- 目标图片 -->
          <div class="upload-col">
            <span class="upload-col-label">{{ $t('tools.face-swap.targetImage') }}</span>
            <div
              class="tool-upload"
              @click="triggerUpload('input')"
              @dragover.prevent
              @drop.prevent="onDrop($event, 'input')"
            >
              <input type="file" ref="inputFileInput" accept="image/*" style="display:none" @change="handleFile($event, 'input')">
              <div v-if="!inputPreviewSrc" class="upload-placeholder">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                <p>{{ $t('tools.detail.clickUpload') }}</p>
                <span>JPG, PNG, WebP</span>
              </div>
              <div v-else class="upload-preview">
                <div class="preview-box preview-box--image">
                  <img v-if="inputPreviewSrc" :src="inputPreviewSrc" :alt="$t('tools.face-swap.targetPreviewAlt')" class="preview-img clickable-preview" @click.stop="openImage(inputPreviewSrc)">
                </div>
                <button class="btn btn-secondary btn-sm" @click.stop="clearUpload('input')">{{ $t('tools.detail.reupload') }}</button>
              </div>
            </div>
          </div>

          <!-- 源人脸图片 -->
          <div class="upload-col">
            <span class="upload-col-label">{{ $t('tools.face-swap.sourceImage') }}</span>
            <div
              class="tool-upload"
              @click="triggerUpload('source')"
              @dragover.prevent
              @drop.prevent="onDrop($event, 'source')"
            >
              <input type="file" ref="sourceFileInput" accept="image/*" style="display:none" @change="handleFile($event, 'source')">
              <div v-if="!sourcePreviewSrc" class="upload-placeholder">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1"><circle cx="12" cy="8" r="5"/><path d="M3 21v-2a7 7 0 0 1 7-7h4a7 7 0 0 1 7 7v2"/></svg>
                <p>{{ $t('tools.detail.clickUpload') }}</p>
                <span>JPG, PNG, WebP</span>
              </div>
              <div v-else class="upload-preview">
                <div class="preview-box preview-box--image">
                  <img v-if="sourcePreviewSrc" :src="sourcePreviewSrc" :alt="$t('tools.face-swap.sourcePreviewAlt')" class="preview-img clickable-preview" @click.stop="openImage(sourcePreviewSrc)">
                </div>
                <button class="btn btn-secondary btn-sm" @click.stop="clearUpload('source')">{{ $t('tools.detail.reupload') }}</button>
              </div>
            </div>
          </div>
              </div>
              <div class="tool-options"><button class="btn btn-primary btn-lg" :disabled="!inputUploaded || !sourceUploaded || processing" @click="process"><svg v-if="processing" class="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg><span>{{ processing ? $t('tools.detail.processing') : $t('tools.face-swap.processButton') }}</span></button></div>
            </div>
          </div>

          <div class="showcase-column">
          <div class="tool-hero"><div class="tool-hero-info"><span class="tool-icon-lg" aria-hidden="true">🎭</span><h1>{{ $t('tools.face-swap.name') }}</h1><p class="tool-hero-desc">{{ $t('brandSeo.toolDescription', { description: $t('tools.face-swap.fullDesc') }) }}</p></div></div>

          <div v-if="!processing && !processed" class="demo-section workspace-panel">
            <h2 class="demo-title">{{ $t('tools.detail.demo') }}</h2>
            <div class="demo-comparison demo-comparison-formula">
              <div class="demo-formula-item"><span class="demo-label">{{ $t('tools.face-swap.targetImage') }}</span><div class="demo-image"><img :src="demoTargetSrc" :alt="$t('tools.face-swap.targetImage')" class="clickable-preview" @click.stop="openImage(demoTargetSrc)"></div></div>
              <div class="demo-operator">+</div>
              <div class="demo-formula-item"><span class="demo-label">{{ $t('tools.face-swap.sourceImage') }}</span><div class="demo-image"><img :src="demoSourceSrc" :alt="$t('tools.face-swap.sourceImage')" class="clickable-preview" @click.stop="openImage(demoSourceSrc)"></div></div>
              <div class="demo-operator">=</div>
              <div class="demo-formula-item"><span class="demo-label">{{ $t('tools.face-swap.effectImageLabel') }}</span><div class="demo-image"><img :src="demoResultSrc" :alt="$t('tools.face-swap.effectImageLabel')" class="clickable-preview" @click.stop="openImage(demoResultSrc)"></div></div>
            </div>
          </div>
          <div v-else-if="processing" class="processing-panel workspace-panel"><div class="workspace-heading"><h2>{{ $t('tools.detail.processing') }}</h2><span class="processing-status"><i></i>{{ $t('tools.face-swap.name') }}</span></div><div class="processing-preview"><img v-if="inputPreviewSrc" :src="inputPreviewSrc" :alt="$t('tools.face-swap.processingAlt')"><div class="processing-overlay"><div class="loading-spinner"></div></div></div><div class="processing-track"><span></span></div></div>
          <div v-else class="result-panel workspace-panel"><div class="workspace-heading"><h2>{{ $t('tools.detail.after') }}</h2><span class="result-status">✓ {{ $t('tools.face-swap.name') }}</span></div><div class="output-comparison"><div class="output-before"><span class="output-label">{{ $t('tools.detail.before') }}</span><div class="output-image output-image--fill"><img v-if="inputPreviewSrc" :src="inputPreviewSrc" :alt="$t('tools.face-swap.resultBeforeAlt')" class="compare-img clickable-preview" @click.stop="openImage(inputPreviewSrc)"></div></div><div class="output-after"><span class="output-label">{{ $t('tools.detail.after') }}</span><div class="output-image output-image--fill"><img v-if="resultSrc" :src="resultSrc" :alt="$t('tools.face-swap.resultAfterAlt')" class="compare-img clickable-preview" @click.stop="openImage(resultSrc)"></div></div></div><div class="output-actions"><button v-if="resultSrc" type="button" class="btn btn-primary" @click="downloadResult">⬇️ {{ $t('tools.detail.download') }}</button><button class="btn btn-secondary" @click="reset">{{ $t('tools.detail.processNew') }}</button></div></div>
          </div>
        </div>
      </section>

      <div class="seo-content-start detail-section feature-section">
        <div class="section-heading"><span class="section-kicker">{{ $t('tools.face-swap.featuresKicker') }}</span><h2>{{ $t('brandSeo.sectionTitle', { title: $t('tools.face-swap.featuresTitle') }) }}</h2><p>{{ $t('tools.face-swap.featuresIntro') }}</p></div>
        <a v-if="features[0]" class="section-primary-cta" href="#face-swap-tool">{{ features[0].cta }}</a>
        <div class="landing-face-formula">
          <div><img :src="demoTargetSrc" :alt="$t('tools.face-swap.targetImage')" class="clickable-preview" @click.stop="openImage(demoTargetSrc)"></div>
          <span>+</span>
          <div><img :src="demoSourceSrc" :alt="$t('tools.face-swap.sourceImage')" class="clickable-preview" @click.stop="openImage(demoSourceSrc)"></div>
          <span>=</span>
          <div><img :src="demoResultSrc" :alt="$t('tools.face-swap.effectImageLabel')" class="clickable-preview" @click.stop="openImage(demoResultSrc)"></div>
        </div>
        <div class="feature-grid"><article v-for="(feature, idx) in features" :key="'feature-' + idx" class="feature-card"><div class="feature-card-image"><img :src="featureImages[idx]" :alt="feature.title" class="clickable-preview" @click.stop="openImage(featureImages[idx])"></div><span class="feature-index">0{{ idx + 1 }}</span><h3>{{ feature.title }}</h3><p>{{ feature.desc }}</p><a href="#face-swap-tool" class="feature-cta">{{ feature.cta }}</a></article></div>
      </div>
      <div class="detail-section use-case-section">
        <div class="section-heading"><span class="section-kicker">{{ $t('tools.face-swap.useCasesKicker') }}</span><h2>{{ $t('tools.face-swap.useCasesTitle') }}</h2><p>{{ $t('tools.face-swap.useCasesIntro') }}</p></div>
        <div class="use-case-list">
          <article v-for="(item, idx) in useCases" :key="'case-' + idx" class="use-case-row">
            <div class="use-case-visual"><img :src="useCaseImages[idx]" :alt="item.imageAlt" class="clickable-preview" @click.stop="openImage(useCaseImages[idx])"></div>
            <div class="use-case-copy"><span class="feature-index">0{{ idx + 1 }}</span><h3>{{ item.title }}</h3><p>{{ item.desc }}</p><a class="feature-cta" href="#face-swap-tool">{{ item.cta }}</a></div>
          </article>
        </div>
      </div>
      <div class="detail-section how-to-section"><div class="section-heading"><span class="section-kicker">{{ $t('tools.face-swap.howToKicker') }}</span><h2>{{ $t('brandSeo.sectionTitle', { title: $t('tools.face-swap.howToTitle') }) }}</h2><p>{{ $t('tools.face-swap.howToIntro') }}</p></div><div class="steps"><div v-for="(step, idx) in steps" :key="idx" class="step"><div class="step-number">{{ idx + 1 }}</div><h3>{{ step.title }}</h3><p>{{ step.desc }}</p></div></div></div>
      <div class="detail-section more-tools-section"><h2>{{ $t('tools.detail.moreTools') }}</h2><div class="grid-5"><ToolListCard v-for="t in otherTools" :key="t.id" :tool-id="t.id" :icon="t.icon" :badge="t.badge" :to="'/tools/' + t.id" /></div></div>
      <div class="detail-section faq-section"><div class="section-heading"><h2>{{ $t('brandSeo.faqTitle', { title: $t('tools.face-swap.faqTitle') }) }}</h2></div><div class="faq-list"><details v-for="(item, idx) in faqs" :key="'faq-' + idx" class="faq-item"><summary>{{ item.q }}</summary><p>{{ item.a }}</p></details></div></div>
      <ToolHowToCta
        :kicker="$t('tools.face-swap.howToKicker')"
        :title="$t('brandSeo.sectionTitle', { title: $t('tools.face-swap.howToTitle') })"
        :intro="$t('tools.face-swap.howToIntro')"
        :steps="steps"
        :cta-label="$t('brandSeo.toolFreeTrialCta')"
        target-id="face-swap-tool"
      />
    </div>
    <div v-if="lightboxSrc" class="image-lightbox" @click="closeImage"><button type="button" class="lightbox-close" :aria-label="$t('common.close')" @click.stop="closeImage">×</button><img :src="lightboxSrc" :alt="$t('tools.face-swap.name')"></div>
      </div>
</template>

<script>
import { submitFaceSwap, pollFaceSwapResult } from '@/api/faceSwap'
import { saveOrUpdateAIHistory, formatCreateDate, uploadFile } from '@/api'
import ToolHowToCta from '@/components/ToolHowToCta.vue'

var MAX_FILE_BYTES = 15 * 1024 * 1024
var DEMO_TARGET_SRC = require('@/assets/images/tool-demos/face-swap-target.webp')
var DEMO_SOURCE_SRC = require('@/assets/images/tool-demos/face-swap-source.webp')
var DEMO_RESULT_SRC = require('@/assets/images/tool-demos/face-swap-result.webp')
var PORTRAIT_USE_CASE_SRC = require('@/assets/images/tool-demos/face-usecase-portrait.webp')
var MEME_USE_CASE_SRC = require('@/assets/images/tool-demos/face-usecase-meme.webp')
var CHARACTER_USE_CASE_SRC = require('@/assets/images/tool-demos/face-usecase-cosplay.webp')

export default {
  name: 'FaceSwapDetail',
  components: {
    ToolHowToCta: ToolHowToCta
  },
  data() {
    return {
      inputUploaded: false,
      sourceUploaded: false,
      inputPreviewSrc: '',
      sourcePreviewSrc: '',
      inputFile: null,
      sourceFile: null,
      processing: false,
      processed: false,
      resultSrc: '',
      processError: null,
      poller: null,
      lightboxSrc: ''
    }
  },
  beforeDestroy() {
    if (this.poller) {
      this.poller.stop()
      this.poller = null
    }
  },
  computed: {
    isLoggedIn() {
      return !!this.$store.state.userInfo
    },
    demoTargetSrc() {
      return DEMO_TARGET_SRC
    },
    demoSourceSrc() {
      return DEMO_SOURCE_SRC
    },
    demoResultSrc() {
      return DEMO_RESULT_SRC
    },
    featureImages: function() {
      return [DEMO_RESULT_SRC, DEMO_TARGET_SRC, DEMO_SOURCE_SRC]
    },
    features: function() {
      return this.$t('tools.face-swap.features')
    },
    useCases: function() {
      return this.$t('tools.face-swap.useCases')
    },
    useCaseImages: function() {
      return [PORTRAIT_USE_CASE_SRC, MEME_USE_CASE_SRC, CHARACTER_USE_CASE_SRC]
    },
    // 合并换脸工具问答与 Miral AI 免费试用说明。
    faqs: function() {
      var toolFaqs = this.$t('tools.face-swap.faqs')
      var brandFaqs = this.$t('brandSeo.toolFaqs')
      return (Array.isArray(toolFaqs) ? toolFaqs : []).concat(Array.isArray(brandFaqs) ? brandFaqs : [])
    },
    otherTools() {
      return this.$store.state.tools.filter(function(t) { return t.id !== 'face-swap' }).slice(0, 5)
    },
    steps() {
      return [
        { title: this.$t('tools.face-swap.step1Title'), desc: this.$t('tools.face-swap.step1Desc') },
        { title: this.$t('tools.face-swap.step2Title'), desc: this.$t('tools.face-swap.step2Desc') },
        { title: this.$t('tools.face-swap.step3Title'), desc: this.$t('tools.face-swap.step3Desc') }
      ]
    }
  },
  methods: {
    // 打开图片全屏预览。
    openImage: function(src) {
      this.lightboxSrc = src || ''
    },
    // 关闭图片全屏预览。
    closeImage: function() {
      this.lightboxSrc = ''
    },
    goLogin() {
      this.$store.dispatch('showLoginModal')
    },
    triggerUpload(type) {
      if (type === 'input' && this.$refs.inputFileInput) this.$refs.inputFileInput.click()
      if (type === 'source' && this.$refs.sourceFileInput) this.$refs.sourceFileInput.click()
    },
    onDrop(e, type) {
      var files = e.dataTransfer && e.dataTransfer.files
      if (files && files[0]) this.readFile(files[0], type)
    },
    handleFile(e, type) {
      var input = e.target
      var file = input.files && input.files[0]
      if (!file) {
        if (input) input.value = ''
        return
      }
      this.readFile(file, type)
      if (input) input.value = ''
    },
    readFile(file, type) {
      var self = this
      this.processError = null
      if (!file.type || file.type.indexOf('image/') !== 0) {
        this.processError = this.$t('tools.face-swap.notImageFile')
        return
      }
      if (file.size > MAX_FILE_BYTES) {
        this.processError = this.$t('tools.face-swap.fileTooLarge')
        return
      }
      var reader = new FileReader()
      reader.onload = function(ev) {
        var src = (ev.target && ev.target.result) ? String(ev.target.result) : ''
        if (type === 'input') {
          self.inputPreviewSrc = src
          self.inputFile = file
          self.inputUploaded = !!src
        } else {
          self.sourcePreviewSrc = src
          self.sourceFile = file
          self.sourceUploaded = !!src
        }
        self.processed = false
        self.resultSrc = ''
      }
      reader.onerror = function() {
        self.processError = self.$t('common.generateFailedContactSupport')
      }
      reader.readAsDataURL(file)
    },
    clearUpload(type) {
      if (type === 'input') {
        this.inputUploaded = false
        this.inputPreviewSrc = ''
        this.inputFile = null
      } else {
        this.sourceUploaded = false
        this.sourcePreviewSrc = ''
        this.sourceFile = null
      }
      this.processed = false
      this.resultSrc = ''
      this.processing = false
      this.processError = null
    },
    reset() {
      this.clearUpload('input')
      this.clearUpload('source')
    },
    downloadResult() {
      if (!this.resultSrc) return
      var url = this.resultSrc
      var name = this.$t('tools.face-swap.downloadName')
      if (url.indexOf('data:') === 0) {
        var a = document.createElement('a')
        a.href = url
        a.download = name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        return
      }
      fetch(url)
        .then(function(r) { return r.blob() })
        .then(function(blob) {
          var u = URL.createObjectURL(blob)
          var a2 = document.createElement('a')
          a2.href = u
          a2.download = name
          document.body.appendChild(a2)
          a2.click()
          document.body.removeChild(a2)
          URL.revokeObjectURL(u)
        })
        .catch(function() {
          window.open(url, '_blank')
        })
    },
    process() {
      var self = this
      if (!this.inputFile || !this.sourceFile) return
      this.processing = true
      this.processed = false
      this.processError = null
      var createDate = formatCreateDate(new Date())
      var aiName = this.$t('tools.face-swap.name')
      var aiDesc = this.$t('tools.face-swap.name')
      var historyId = null
      var originalUrl = ''

      // 1. 上传目标图到服务器
      uploadFile(this.inputFile).then(function(url) {
        originalUrl = url
      }).catch(function() {
        self.processing = false
        self.processError = self.$t('tools.detail.uploadOriginalFailed')
        return Promise.reject('uploadOriginalFailed')
      }).then(function() {
        // 2. 创建历史记录
        return saveOrUpdateAIHistory({
          aiName: aiName,
          taskId: '',
          aiDesc: aiDesc,
          state: 1,
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        })
      }).then(function(hRes) {
        historyId = hRes && (hRes.historyId != null ? hRes.historyId : null)
      }).then(function() {
        // 3. 提交换脸任务
        return submitFaceSwap(self.inputFile, self.sourceFile)
      }).then(function(createId) {
        // 4. 轮询结果
        self.poller = pollFaceSwapResult(createId, function(err, url) {
          self.poller = null
          if (err) {
            self.onFailed(err, historyId, aiName, aiDesc, originalUrl, createDate)
          } else {
            self.onSuccess(url, historyId, aiName, aiDesc, originalUrl, createDate)
          }
        })
      }).catch(function(err) {
        if (err === 'uploadOriginalFailed') return
        self.processing = false
        self.processed = false
        self.resultSrc = ''
        self.processError = self.$userFacingError(err, self.$t('common.generateFailedContactSupport'))
        saveOrUpdateAIHistory({
          aiName: aiName,
          historyId: historyId,
          taskId: '',
          aiDesc: aiDesc,
          state: 3,
          message: (err && err.message) ? err.message : '',
          aiResultUrl: '',
          image_large_url: originalUrl,
          aiMediaType: 'image',
          createDate: createDate
        }).catch(function() {})
      })
    },
    onSuccess(blobUrl, historyId, aiName, aiDesc, originalUrl, createDate) {
      var self = this
      self.processing = false
      self.resultSrc = blobUrl
      self.processed = true
      self.processError = null
      fetch(blobUrl).then(function(r) { return r.blob() }).then(function(blob) {
        var file = new File([blob], 'face-swap-result.png', { type: blob.type || 'image/png' })
        return uploadFile(file)
      }).then(function(httpsUrl) {
        return saveOrUpdateAIHistory({
          aiName: aiName || self.$t('tools.face-swap.name'),
          historyId: historyId,
          taskId: '',
          aiDesc: aiDesc || self.$t('tools.face-swap.name'),
          state: 2,
          aiResultUrl: httpsUrl || '',
          image_large_url: originalUrl || '',
          aiMediaType: 'image',
          createDate: createDate || formatCreateDate(new Date())
        })
      }).catch(function() {
        /* 上传失败，仍用原始 URL 标记成功，确保后端记录更新为 state:2 */
        saveOrUpdateAIHistory({
          aiName: aiName || self.$t('tools.face-swap.name'),
          historyId: historyId,
          taskId: '',
          aiDesc: aiDesc || self.$t('tools.face-swap.name'),
          state: 2,
          aiResultUrl: blobUrl || '',
          image_large_url: originalUrl || '',
          aiMediaType: 'image',
          createDate: createDate || formatCreateDate(new Date())
        }).catch(function() {})
      })
    },
    onFailed(err, historyId, aiName, aiDesc, originalUrl, createDate) {
      this.processing = false
      this.processed = false
      this.resultSrc = ''
      this.processError = this.$userFacingError(err, this.$t('common.generateFailedContactSupport'))
      saveOrUpdateAIHistory({
        aiName: aiName || this.$t('tools.face-swap.name'),
        historyId: historyId,
        taskId: '',
        aiDesc: aiDesc || this.$t('tools.face-swap.name'),
        state: 3,
        message: (err && err.message) ? err.message : '',
        aiResultUrl: '',
        image_large_url: originalUrl || '',
        aiMediaType: 'image',
        createDate: createDate || formatCreateDate(new Date())
      }).catch(function() {})
    }
  }
}
</script>

<style scoped>
@import './ModelDetailStyles.css';

.tool-detail-page { padding: 48px 0 80px; }
.breadcrumb { display: flex; align-items: center; gap: 8px; margin-bottom: 32px; font-size: 13px; color: var(--text-muted); }
.breadcrumb a { color: var(--text-secondary); }
.breadcrumb a:hover { color: var(--accent); }
.tool-hero { margin-bottom: 32px; }
.tool-hero-info { text-align: center; max-width: 640px; margin: 0 auto; }
.tool-icon-lg { font-size: 48px; display: block; margin-bottom: 16px; }
.tool-hero-info h1 { font-size: 36px; font-weight: 800; margin-bottom: 12px; }
.tool-hero-desc { font-size: 16px; color: var(--text-secondary); line-height: 1.7; margin-bottom: 24px; }
.demo-section { margin: 32px auto 0; padding: 24px; background: var(--bg-primary); border-radius: var(--radius-lg); max-width: 760px; }
.demo-title { font-size: 16px; font-weight: 600; text-align: center; margin-bottom: 20px; color: var(--text-primary); }
.demo-comparison-formula {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
}
.demo-formula-item { min-width: 0; }
.demo-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; text-align: center; }
.demo-image { aspect-ratio: 3/4; border-radius: var(--radius-lg); overflow: hidden; background: var(--bg-secondary); }
.demo-image img { width: 100%; height: 100%; object-fit: cover; display: block; }
.demo-operator { color: var(--text-primary); font-size: 28px; font-weight: 700; line-height: 1; }
.login-prompt { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-xl); padding: 24px 28px; text-align: center; margin: 32px auto 0; max-width: 680px; }
.login-prompt-content { max-width: 360px; margin: 0 auto; }
.login-prompt-content svg { display: block; margin: 0 auto; }
.login-prompt h2 { margin: 6px 0 4px; font-size: 20px; }
.login-prompt p { color: var(--text-muted); font-size: 14px; margin-bottom: 12px; }
.tool-interface { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-xl); padding: 32px; margin: 32px auto 0; max-width: 680px; }
.upload-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.upload-col { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.upload-col-label { font-size: 13px; font-weight: 600; color: var(--text-secondary); }
.tool-upload { cursor: pointer; width: 100%; }
.upload-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px 16px; border: 2px dashed var(--border-color); border-radius: var(--radius-lg); transition: border-color 0.2s; }
.upload-placeholder:hover { border-color: var(--accent); }
.upload-placeholder p { margin-top: 12px; font-size: 13px; color: var(--text-secondary); }
.upload-placeholder span { font-size: 12px; color: var(--text-muted); }
.upload-preview { display: flex; flex-direction: column; align-items: center; gap: 12px; }
.preview-box { width: 180px; height: 180px; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; overflow: hidden; background: linear-gradient(135deg, #1a1a2e, #0f3460); }
.preview-box--image { padding: 0; }
.preview-img { max-width: 100%; max-height: 100%; width: auto; height: auto; object-fit: contain; display: block; }
.btn-sm { font-size: 12px; padding: 4px 12px; }
.tool-options { display: flex; align-items: flex-end; gap: 16px; margin-top: 16px; justify-content: center; }
.tool-result { margin-top: 32px; }
.gen-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; gap: 12px; }
.loading-spinner { width: 40px; height: 40px; border: 3px solid var(--border-color); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.output-comparison { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.output-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; }
.output-image { aspect-ratio: 1; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; }
.output-image--fill { padding: 0; overflow: hidden; background: var(--bg-primary); }
.compare-img { width: 100%; height: 100%; object-fit: contain; display: block; min-height: 200px; max-height: 420px; background: var(--bg-primary); }
.output-actions { display: flex; gap: 12px; justify-content: center; margin-top: 12px; }
.detail-section { margin-bottom: 64px; }
.detail-section h2 { font-size: 24px; font-weight: 700; margin-bottom: 24px; }
.steps { display: flex; justify-content: center; gap: 48px; flex-wrap: wrap; }
.step { text-align: center; }
.step-number { width: 40px; height: 40px; border-radius: 50%; background: var(--accent); color: #000; display: flex; align-items: center; justify-content: center; font-weight: 700; margin: 0 auto 12px; }
.step h3 { margin-bottom: 6px; }
.step p { font-size: 13px; color: var(--text-muted); }
.spinner { animation: spin 1s linear infinite; }
.tool-detail-page { padding: 0 0 80px; background: #fff; overflow-x: hidden; }
.first-screen { display: flex; flex-direction: column; width: calc(100vw - 72px); min-height: 100vh; box-sizing: border-box; margin-left: calc(50% - 50vw + 36px); padding: 24px 38px 38px; background: radial-gradient(circle at 82% 8%, rgba(45, 212, 191, .12), transparent 30%), #f7f9fc; }
.first-screen .breadcrumb { width: 100%; max-width: 1180px; margin: 0 auto 20px; }
.first-screen .tool-hero { margin-bottom: 28px; }
.first-screen .tool-hero-info { max-width: 820px; }
.first-screen .tool-hero-info h1 { margin: 0 0 12px; color: #111827; font-size: 42px; line-height: 1.15; }
.first-screen .tool-hero-desc { max-width: 760px; margin: 0 auto; color: #65717c; }
.first-screen .tool-icon-lg { margin-bottom: 10px; font-size: 42px; }
.first-screen-grid { display: grid; grid-template-columns: minmax(430px, .9fr) minmax(560px, 1.1fr); align-items: center; gap: 30px; width: 100%; max-width: 1180px; margin: 0 auto; flex: 1; }
.action-column { min-width: 0; }
.first-screen .tool-interface, .first-screen .login-prompt { width: 100%; max-width: none; min-height: 500px; box-sizing: border-box; margin: 0; padding: 20px; border: 1px solid #dfe7eb; border-radius: 18px; background: rgba(255,255,255,.96); box-shadow: 0 14px 36px rgba(35,55,68,.07); }
.first-screen .login-prompt { display: flex; align-items: center; justify-content: center; }
.first-screen .upload-grid { grid-template-columns: 1fr 1fr; gap: 12px; }
.first-screen .tool-upload { min-height: 250px; padding: 0; }
.first-screen .upload-placeholder { min-height: 220px; box-sizing: border-box; padding: 20px 12px; }
.first-screen .preview-box { width: 100%; height: 220px; background: #edf1f3; }
.tool-error--block { margin-bottom: 12px; text-align: center; }
.tool-error { margin: 0; color: #c94343; font-size: 13px; }
.workspace-panel { width: 100%; min-height: 500px; box-sizing: border-box; margin: 0; padding: 18px; border: 1px solid #e2e8ec; border-radius: 18px; background: #fff; box-shadow: 0 16px 40px rgba(35,55,68,.08); }
.workspace-panel .demo-title { display: block; margin: 0 0 18px; color: #202731; }
.workspace-panel .demo-comparison-formula { min-height: 420px; grid-template-columns: 1fr auto 1fr auto 1fr; gap: 10px; }
.workspace-panel .demo-formula-item { min-width: 0; }
.workspace-panel .demo-image { aspect-ratio: 3 / 4; }
.workspace-panel .demo-operator { font-size: 24px; }
.workspace-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.workspace-heading h2 { margin: 0; color: #202731; font-size: 18px; }
.processing-status, .result-status { display: inline-flex; align-items: center; gap: 7px; color: #07856f; font-size: 12px; font-weight: 700; }
.processing-status i { width: 7px; height: 7px; border-radius: 50%; background: #0aa889; box-shadow: 0 0 0 5px rgba(10,168,137,.12); }
.processing-preview { position: relative; height: 400px; overflow: hidden; border-radius: 14px; background: #edf1f3; }
.processing-preview img { width: 100%; height: 100%; object-fit: contain; }
.processing-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(247,250,251,.55); }
.processing-track { height: 5px; overflow: hidden; margin-top: 16px; border-radius: 99px; background: #e8eeef; }
.processing-track span { display: block; width: 42%; height: 100%; border-radius: inherit; background: #0aa889; animation: processing-slide 1.4s ease-in-out infinite alternate; }
@keyframes processing-slide { to { transform: translateX(138%); } }
.workspace-panel .output-comparison { gap: 12px; }
.workspace-panel .output-image { min-height: 360px; background: #edf1f3; }
.clickable-preview { cursor: zoom-in; }
.seo-content-start { margin-top: 90px; }
.detail-section { max-width: 1180px; margin: 0 auto 92px; }
.section-heading { max-width: 780px; margin: 0 auto 42px; text-align: center; }
.section-heading h2 { margin: 8px 0 12px; color: #15202b; font-size: 34px; line-height: 1.22; }
.section-heading p { margin: 0; color: #697681; line-height: 1.75; }
.section-kicker { color: #07856f; font-size: 12px; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; }
.feature-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
.feature-card { padding: 16px 16px 26px; border: 1px solid #e3e9ec; border-radius: 20px; background: #fff; box-shadow: 0 14px 34px rgba(33,53,65,.07); }
.feature-card-image { height: 280px; overflow: hidden; margin-bottom: 20px; border-radius: 14px; background: #edf1f3; }
.feature-card-image img { width: 100%; height: 100%; object-fit: cover; }
.feature-index { color: #0aa889; font-size: 13px; font-weight: 800; letter-spacing: .12em; }
.feature-card h3 { margin: 10px 0; color: #17212b; font-size: 21px; }
.feature-card p { color: #697681; line-height: 1.72; }
.feature-cta { display: inline-flex; align-items: center; justify-content: center; min-height: 46px; margin-top: 8px; padding: 0 24px; border: 1px solid #0a9c82; border-radius: 999px; color: #087d68; font-size: 14px; font-weight: 750; transition: color .2s, background .2s; }
.feature-cta:hover { color: #fff; background: #0a9c82; }
.seo-card-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.seo-card { padding: 26px; border: 1px solid #e4eaed; border-radius: 18px; background: #fbfcfd; }
.seo-card h3 { margin: 0 0 10px; color: #202a34; font-size: 18px; }
.seo-card p { margin: 0; color: #707d87; line-height: 1.7; }
.use-case-list { display: flex; flex-direction: column; gap: 100px; }
.use-case-row { display: grid; grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr); align-items: center; gap: 72px; }
.use-case-row:nth-child(even) .use-case-visual { order: 2; }
.use-case-row:nth-child(even) .use-case-copy { order: 1; }
.use-case-visual { height: 390px; overflow: hidden; border-radius: 24px; background: #eef2f4; box-shadow: 0 20px 55px rgba(31, 50, 63, .13); }
.use-case-visual img { width: 100%; height: 100%; object-fit: cover; }
.use-case-copy { max-width: 440px; }
.use-case-copy h3 { margin: 14px 0 16px; color: #17212b; font-size: clamp(28px, 3vw, 40px); line-height: 1.18; }
.use-case-copy p { margin: 0 0 26px; color: #697681; font-size: 16px; line-height: 1.8; }
.how-to-section .steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.how-to-section .step { padding: 26px 22px; border: 1px solid #e5ebee; border-radius: 17px; background: #fff; }
.faq-list { max-width: 900px; margin: 0 auto; }
.faq-item { margin-bottom: 10px; padding: 0 22px; border: 1px solid #e7edf1; border-radius: 15px; background: #fbfcfd; }
.faq-item summary { position: relative; cursor: pointer; padding: 20px 38px 20px 0; font-weight: 750; list-style: none; }
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after { content: '+'; position: absolute; top: 15px; right: 0; width: 30px; height: 30px; border-radius: 50%; background: #e8f8f3; color: #07856f; font-size: 20px; line-height: 28px; text-align: center; }
.faq-item[open] summary::after { content: '−'; }
.faq-item p { margin: 0; padding: 0 38px 20px 0; color: #697681; line-height: 1.7; }
.image-lightbox { position: fixed; z-index: 3000; inset: 0; display: flex; align-items: center; justify-content: center; padding: 48px; background: rgba(7,12,16,.88); cursor: zoom-out; }
.image-lightbox img { max-width: 94vw; max-height: 90vh; object-fit: contain; border-radius: 10px; }
.lightbox-close { position: absolute; top: 20px; right: 24px; border: 0; background: transparent; color: #fff; font-size: 38px; cursor: pointer; }
@media (max-width: 1100px) { .first-screen-grid { grid-template-columns: 1fr; max-width: 760px; } .first-screen { min-height: auto; } }
@media (max-width: 768px) {
  .first-screen { width: 100vw; margin-left: calc(50% - 50vw); padding: 18px 16px 28px; }
  .first-screen .tool-hero-info h1 { font-size: 34px; }
  .first-screen .tool-interface, .first-screen .login-prompt, .workspace-panel { min-height: auto; }
  .first-screen .upload-grid { grid-template-columns: 1fr; }
  .workspace-panel .demo-comparison-formula { min-height: 0; grid-template-columns: 1fr; }
  .workspace-panel .demo-operator { text-align: center; }
  .processing-preview { height: 320px; }
  .workspace-panel .output-comparison { grid-template-columns: 1fr; }
  .workspace-panel .output-image { min-height: 260px; }
  .seo-content-start { margin-top: 68px; }
  .detail-section { margin-bottom: 68px; }
  .section-heading h2 { font-size: 28px; }
  .feature-grid, .seo-card-grid, .how-to-section .steps { grid-template-columns: 1fr; }
  .feature-card-image { height: 320px; }
  .use-case-list { gap: 64px; }
  .use-case-row { grid-template-columns: 1fr; gap: 28px; }
  .use-case-row:nth-child(even) .use-case-visual, .use-case-row:nth-child(even) .use-case-copy { order: initial; }
  .use-case-visual { height: 300px; }
  .image-lightbox { padding: 20px; }
}

/* Pollo 参考页排版：首屏独立，SEO 内容从下一屏开始。 */
.pollo-layout { padding-bottom: 64px; }
.pollo-layout .first-screen { position: relative; width: calc(100vw - 48px); min-height: calc(100vh - 24px); margin-left: calc(50% - 50vw + 24px); padding: 26px 32px 30px; }
.pollo-layout .first-screen .breadcrumb { position: absolute; z-index: 3; top: 42px; left: 48px; width: 410px; margin: 0; }
.pollo-layout .first-screen .tool-hero { width: 100%; margin: 0; }
.pollo-layout .first-screen .tool-hero-info { max-width: 760px; margin: 0 auto; text-align: center; }
.pollo-layout .first-screen .tool-hero-info h1 { font-size: 36px; line-height: 1.32; }
.pollo-layout .first-screen .tool-icon-lg { display: none; }
.pollo-layout .first-screen-grid { grid-template-columns: 468px minmax(0, 1fr); align-items: stretch; gap: 72px; max-width: 1392px; min-height: calc(100vh - 84px); }
.pollo-layout .first-screen .showcase-column { display: flex; min-width: 0; flex-direction: column; justify-content: center; gap: 28px; }
.pollo-layout .first-screen .action-column { display: flex; min-height: 0; box-sizing: border-box; padding-top: 54px; }
.pollo-layout .first-screen .tool-interface,
.pollo-layout .first-screen .login-prompt { min-height: 0; padding: 82px 20px 20px; border-radius: 16px; }
.pollo-layout .first-screen .tool-interface { display: flex; width: 100%; height: auto; flex: 1; flex-direction: column; padding-top: 28px; }
.pollo-layout .first-screen .upload-grid { display: grid; min-height: 0; flex: 1; grid-template-columns: 1fr; grid-template-rows: repeat(2, minmax(0, 1fr)); gap: 16px; }
.pollo-layout .first-screen .upload-col { min-height: 0; align-items: stretch; }
.pollo-layout .first-screen .tool-upload { display: flex; min-height: 0; flex: 1; }
.pollo-layout .first-screen .upload-placeholder,
.pollo-layout .first-screen .upload-preview { width: 100%; height: 100%; box-sizing: border-box; }
.pollo-layout .first-screen .upload-placeholder { min-height: 0; }
.pollo-layout .first-screen .upload-preview { justify-content: center; }
.pollo-layout .first-screen .preview-box { height: 150px; }
.pollo-layout .first-screen .tool-options { width: 100%; flex: 0 0 auto; align-items: stretch; justify-content: stretch; }
.pollo-layout .first-screen .tool-options .btn { width: 100%; justify-content: center; text-align: center; }
.pollo-layout .first-screen .showcase-column > .workspace-panel { width: min(710px, 100%); min-height: 0; margin: 0 auto; }
.pollo-layout .first-screen .workspace-panel .demo-comparison-formula { min-height: 406px; }
.pollo-layout .first-screen .processing-preview { height: 390px; }
.pollo-layout .seo-content-start { margin-top: 0; padding-top: 112px; }
.pollo-layout .detail-section { margin-bottom: 112px; }
.pollo-layout .section-heading { max-width: 800px; margin-bottom: 30px; }
.pollo-layout .section-heading h2,
.pollo-layout .detail-section > h2 { font-size: 40px; line-height: 1.4; }
.pollo-layout .section-heading p { font-size: 16px; }
.section-primary-cta { display: table; min-width: 190px; margin: 0 auto 48px; padding: 13px 28px; border: 1px solid #0a9c82; border-radius: 999px; color: #087d68; font-size: 15px; font-weight: 750; text-align: center; }
.section-primary-cta:hover { color: #fff; background: #0a9c82; }
.landing-face-formula { display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr); align-items: center; gap: 16px; width: min(880px, 100%); margin: 0 auto 112px; }
.landing-face-formula > div { height: 390px; overflow: hidden; border-radius: 16px; background: #edf1f3; box-shadow: 0 20px 55px rgba(31, 50, 63, .13); }
.landing-face-formula img { width: 100%; height: 100%; object-fit: cover; }
.landing-face-formula > span { color: #53606b; font-size: 30px; font-weight: 800; }
.pollo-layout .feature-grid { display: flex; flex-direction: column; gap: 96px; }
.pollo-layout .feature-card { display: grid; grid-template-columns: minmax(0, 480px) minmax(320px, 480px); grid-template-rows: auto auto auto auto; justify-content: space-between; align-items: center; column-gap: 72px; padding: 0; border: 0; border-radius: 0; box-shadow: none; }
.pollo-layout .feature-card-image { grid-row: 1 / 5; width: 100%; height: 320px; margin: 0; border-radius: 16px; box-shadow: 0 20px 55px rgba(31, 50, 63, .13); }
.pollo-layout .feature-card > .feature-index { align-self: end; }
.pollo-layout .feature-card h3 { margin: 12px 0; color: #17212b; font-size: 40px; line-height: 1.4; }
.pollo-layout .feature-card p { margin: 0; color: #697681; font-size: 16px; line-height: 1.75; }
.pollo-layout .feature-card .feature-cta { justify-self: start; }
.pollo-layout .feature-card:nth-child(even) .feature-card-image { grid-column: 2; }
.pollo-layout .feature-card:nth-child(even) > :not(.feature-card-image) { grid-column: 1; }
.pollo-layout .use-case-list { gap: 96px; }
.pollo-layout .use-case-row { grid-template-columns: minmax(0, 480px) minmax(320px, 480px); justify-content: space-between; gap: 72px; }
.pollo-layout .use-case-visual { height: 320px; border-radius: 16px; }
.pollo-layout .use-case-copy h3 { font-size: 40px; line-height: 1.4; }
.pollo-layout .how-to-section .steps { gap: 24px; }
.pollo-layout .how-to-section .step { min-height: 142px; padding: 30px; text-align: center; }
.pollo-layout .how-to-section .step-number { margin-right: auto; margin-left: auto; }
.pollo-layout .more-tools-section > h2 { margin-bottom: 44px; text-align: center; }
.pollo-layout .more-tools-section .grid-5 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.pollo-layout .more-tools-section .grid-5 > :nth-child(n + 5) { display: none; }
.pollo-layout .faq-section { max-width: 960px; margin-right: auto; margin-bottom: 48px; margin-left: auto; padding: 74px 36px; }
.pollo-layout .faq-section .section-heading { margin-right: auto; margin-left: auto; text-align: center; }
.pollo-layout .tool-bottom-how-to { margin-top: 0; }
.pollo-layout .faq-item { margin: 0; padding: 0 8px; border-width: 0 0 1px; border-radius: 0; background: transparent; }
.pollo-layout .faq-item summary { padding: 23px 42px 23px 28px; }
.pollo-layout .faq-item summary::after { right: auto; left: 0; background: transparent; color: #53606b; }
.pollo-layout .faq-item p { padding: 0 42px 22px 28px; }

@media (max-width: 1100px) {
  .pollo-layout .first-screen { min-height: auto; }
  .pollo-layout .first-screen .breadcrumb,
  .pollo-layout .first-screen .tool-hero { position: static; width: auto; margin: 0 auto 24px; }
  .pollo-layout .first-screen-grid { grid-template-columns: 1fr; max-width: 760px; min-height: 0; }
  .pollo-layout .first-screen .action-column { padding-top: 0; }
  .pollo-layout .first-screen .tool-interface,
  .pollo-layout .first-screen .login-prompt { padding-top: 24px; }
  .pollo-layout .first-screen .showcase-column > .workspace-panel { margin-top: 0; }
}

@media (max-width: 768px) {
  .pollo-layout { padding-bottom: 32px; }
  .pollo-layout .first-screen { width: 100vw; margin-left: calc(50% - 50vw); padding: 20px 16px 30px; }
  .pollo-layout .first-screen .showcase-column { display: none; }
  .pollo-layout .first-screen .breadcrumb { margin-bottom: 16px; }
  .pollo-layout .first-screen .tool-interface,
  .pollo-layout .first-screen .login-prompt { min-height: 610px; padding: 20px 12px; }
  .pollo-layout .seo-content-start { padding-top: 76px; }
  .pollo-layout .detail-section { margin-bottom: 76px; }
  .pollo-layout .section-heading h2,
  .pollo-layout .detail-section > h2 { font-size: 28px; }
  .section-primary-cta { margin-bottom: 34px; }
  .landing-face-formula { grid-template-columns: 1fr; margin-bottom: 72px; }
  .landing-face-formula > div { height: 360px; }
  .landing-face-formula > span { text-align: center; }
  .pollo-layout .feature-grid,
  .pollo-layout .use-case-list { gap: 68px; }
  .pollo-layout .feature-card { grid-template-columns: 1fr; grid-template-rows: auto; gap: 0; text-align: center; }
  .pollo-layout .feature-card-image,
  .pollo-layout .feature-card:nth-child(even) .feature-card-image { grid-column: 1; grid-row: auto; height: 320px; margin-bottom: 22px; }
  .pollo-layout .feature-card > :not(.feature-card-image),
  .pollo-layout .feature-card:nth-child(even) > :not(.feature-card-image) { grid-column: 1; }
  .pollo-layout .feature-card h3,
  .pollo-layout .use-case-copy h3 { font-size: 24px; }
  .pollo-layout .feature-card .feature-cta { justify-self: center; }
  .pollo-layout .use-case-row { gap: 22px; text-align: center; }
  .pollo-layout .use-case-visual,
  .pollo-layout .use-case-row:nth-child(even) .use-case-visual { height: 320px; order: 1; }
  .pollo-layout .use-case-copy,
  .pollo-layout .use-case-row:nth-child(even) .use-case-copy { max-width: none; order: 2; }
  .pollo-layout .more-tools-section .grid-5 { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .pollo-layout .faq-section { margin-bottom: 32px; padding: 48px 0; }
}
</style>
